import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Archive, ChevronDown, ChevronUp, History, Search } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FILTERS = {
  customer: "",
  brand: "",
  sku_name: "",
  manufacturer: "",
  can_size: "",
  artwork_po: "",
  customer_po: "",
  ball_project_number: "",
  upc: "",
  sales_ops: "",
  osr: "",
  completion_from: "",
  completion_to: "",
  application_type: "",
  beverage_type: "",
  beer_type: "",
  interim_status_tag: "",
  filler_copacker: "",
};

const FILTER_FIELDS = [
  ["customer", "Customer"],
  ["brand", "Brand"],
  ["sku_name", "SKU Name"],
  ["manufacturer", "Manufacturer"],
  ["can_size", "Can Size"],
  ["artwork_po", "Artwork PO"],
  ["customer_po", "Customer PO"],
  ["ball_project_number", "Ball Project Number"],
  ["upc", "UPC"],
  ["sales_ops", "Sales Ops"],
  ["osr", "OSR"],
  ["application_type", "Application Type"],
  ["beverage_type", "Beverage Type"],
  ["beer_type", "Beer Type"],
  ["interim_status_tag", "Interim Status Tag"],
  ["filler_copacker", "Filler / Copacker"],
];

const firstSkuValue = (project, field) => {
  const items = project?.sku_items || [];
  return items.find((item) => item?.[field])?.[field] || "";
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
};

export const ArchivePageV17 = () => {
  const navigate = useNavigate();
  const { canCreateProject } = useAuth();
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [creatingRevision, setCreatingRevision] = useState("");

  const loadArchive = async (nextQuery = query, nextFilters = filters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (nextQuery.trim()) params.set("q", nextQuery.trim());
      Object.entries(nextFilters).forEach(([key, value]) => {
        if (String(value || "").trim()) params.set(key, String(value).trim());
      });
      const response = await axios.get(`${API}/archive/projects?${params.toString()}`);
      setProjects(response.data?.projects || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load completed project archive");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadArchive("", EMPTY_FILTERS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitSearch = (event) => {
    event.preventDefault();
    loadArchive();
  };

  const clearFilters = () => {
    setQuery("");
    setFilters(EMPTY_FILTERS);
    loadArchive("", EMPTY_FILTERS);
  };

  const createRevision = async (project) => {
    const reason = window.prompt(`Revision reason for ${project.name}:`);
    if (reason === null) return;
    if (!reason.trim()) {
      toast.error("Revision reason is required");
      return;
    }

    setCreatingRevision(project.id);
    try {
      const response = await axios.post(`${API}/projects/${project.id}/revisions`, {
        revision_reason: reason.trim(),
      });
      toast.success(`Revision ${response.data.revision_number} created`);
      navigate(`/projects/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create revision");
    } finally {
      setCreatingRevision("");
    }
  };

  return (
    <div className="animate-fade-in space-y-6">
      <div>
        <div className="flex items-center gap-3">
          <Archive size={28} />
          <h1 className="text-3xl font-bold">Completed Projects Archive</h1>
        </div>
        <p className="text-muted-foreground mt-2">
          Search completed Graphics projects and open their full historical project record.
        </p>
      </div>

      <form onSubmit={submitSearch} className="bg-card border border-border rounded-xl p-5 space-y-4">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search customer, brand, SKU, PO, Ball number, UPC, rep, filler, or project name..."
              className="w-full pl-10 pr-4 py-2.5 bg-secondary border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowFilters((value) => !value)}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-secondary border border-border rounded-lg font-medium"
          >
            {showFilters ? <ChevronUp size={17} /> : <ChevronDown size={17} />}
            Filters
          </button>
          <button type="submit" className="px-5 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium">
            Search Archive
          </button>
          <button type="button" onClick={clearFilters} className="px-4 py-2.5 border border-border rounded-lg text-muted-foreground hover:text-foreground">
            Clear
          </button>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 pt-4 border-t border-border">
            {FILTER_FIELDS.map(([key, label]) => (
              <label key={key} className="space-y-1">
                <span className="text-xs text-muted-foreground">{label}</span>
                <input
                  value={filters[key]}
                  onChange={(event) => setFilters((previous) => ({ ...previous, [key]: event.target.value }))}
                  className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm"
                />
              </label>
            ))}
            <label className="space-y-1">
              <span className="text-xs text-muted-foreground">Completion Date From</span>
              <input
                type="date"
                value={filters.completion_from}
                onChange={(event) => setFilters((previous) => ({ ...previous, completion_from: event.target.value }))}
                className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm"
              />
            </label>
            <label className="space-y-1">
              <span className="text-xs text-muted-foreground">Completion Date To</span>
              <input
                type="date"
                value={filters.completion_to}
                onChange={(event) => setFilters((previous) => ({ ...previous, completion_to: event.target.value }))}
                className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-sm"
              />
            </label>
          </div>
        )}
      </form>

      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {loading ? "Searching archive..." : `${projects.length} completed project${projects.length === 1 ? "" : "s"} found`}
        </p>
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-muted-foreground animate-pulse">Loading completed projects...</div>
        ) : projects.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            <Archive size={44} className="mx-auto mb-3 opacity-50" />
            <p className="font-medium text-foreground">No archived projects found</p>
            <p className="text-sm mt-1">Adjust the search or filters and try again.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table min-w-[1100px]">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Customer</th>
                  <th>Brand / SKU</th>
                  <th>Manufacturer</th>
                  <th>Can Size</th>
                  <th>Sales Ops / OSR</th>
                  <th>Completed</th>
                  <th>Revision</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {projects.map((project) => {
                  const skuName = project.sku_name || project.sku_flavor || firstSkuValue(project, "sku_name");
                  return (
                    <tr key={project.id}>
                      <td>
                        <Link to={`/projects/${project.id}`} className="font-medium hover:underline">
                          {project.name}
                        </Link>
                        {project.ball_project_number && (
                          <p className="text-xs text-muted-foreground mt-1">Ball {project.ball_project_number}</p>
                        )}
                      </td>
                      <td>{project.customer_name || "—"}</td>
                      <td>
                        <div>{project.brand || firstSkuValue(project, "brand") || "—"}</div>
                        {skuName && <div className="text-xs text-muted-foreground mt-1">{skuName}</div>}
                      </td>
                      <td>{project.manufacturer_name || "—"}</td>
                      <td>{project.can_size || firstSkuValue(project, "can_size") || "—"}</td>
                      <td>
                        <div>{project.isr || "—"}</div>
                        {project.osr && <div className="text-xs text-muted-foreground mt-1">{project.osr}</div>}
                      </td>
                      <td>{formatDate(project.completed_at)}</td>
                      <td>R{project.revision_number || 0}</td>
                      <td>
                        <div className="flex justify-end gap-2">
                          <Link
                            to={`/projects/${project.id}`}
                            className="px-3 py-2 bg-secondary border border-border rounded-lg text-sm font-medium whitespace-nowrap"
                          >
                            Open Project
                          </Link>
                          {canCreateProject && (
                            <button
                              type="button"
                              onClick={() => createRevision(project)}
                              disabled={creatingRevision === project.id}
                              className="inline-flex items-center gap-2 px-3 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium whitespace-nowrap disabled:opacity-50"
                            >
                              <History size={15} />
                              {creatingRevision === project.id ? "Creating..." : "Create Revision"}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
